import type { MobileBottomNavItem, MobileDrawerSection } from "./mobile-navigation.models";
import { mobilePath } from "./mobile-routes.constants";

export const DERIVED_PORTAL_MOBILE_APP_CODES = new Set([
  "academy-student-mobile",
  "academy-mentor-mobile",
  "academy-enterprise-mobile",
  "academy-supervisor-mobile",
]);

const PRESENCE_ROUTE = mobilePath("presence");

const SURVEY_ROUTE_SUFFIXES = [
  "elearning/student/surveys",
  "elearning/mentor/surveys",
  "elearning/contact/surveys",
  "elearning/supervisor/surveys",
] as const;

export function isDerivedPortalMobileAppCode(appCode: string | undefined): boolean {
  const normalized = appCode?.trim().toLowerCase();
  return !!normalized && DERIVED_PORTAL_MOBILE_APP_CODES.has(normalized);
}

export function isElearningSurveysRoute(route: string): boolean {
  const normalized = route.split("?")[0].replace(/\/+$/, "").toLowerCase();
  return SURVEY_ROUTE_SUFFIXES.some(
    (suffix) => normalized === mobilePath(suffix).toLowerCase() || normalized.endsWith(`/${suffix}`)
  );
}

export function isPresenceRoute(route: string): boolean {
  const normalized = route.split("?")[0].replace(/\/+$/, "").toLowerCase();
  return normalized === PRESENCE_ROUTE.toLowerCase() || /(^|\/)(presence-sign|presence)(\/|$)/i.test(normalized);
}

export function resolvePortalFallbackBottomNavItems(
  sections: MobileDrawerSection[],
  isSessionsRoute: (route: string) => boolean,
  finalizeSessionsItem: (item: MobileBottomNavItem) => MobileBottomNavItem
): MobileBottomNavItem[] {
  const businessItems = sections.flatMap((section) =>
    section.Items.filter((item) => !item.IsTaskbar).map((item) => ({ section, item }))
  );

  const out: MobileBottomNavItem[] = [];
  const pushUnique = (candidate: MobileBottomNavItem) => {
    if (!out.some((entry) => entry.Route === candidate.Route)) {
      out.push(candidate);
    }
  };

  const sessionsEntry = businessItems.find(({ item }) => isSessionsRoute(item.Route));
  if (sessionsEntry) {
    pushUnique(
      finalizeSessionsItem({
        LabelKey: sessionsEntry.item.LabelKey,
        Icon: sessionsEntry.item.Icon || sessionsEntry.section.Icon || "school",
        Route: sessionsEntry.item.Route,
        Label: sessionsEntry.item.Label || sessionsEntry.section.Label,
        ShortLabel: "Sessions",
      })
    );
  }

  const surveysEntry = businessItems.find(({ item }) => isElearningSurveysRoute(item.Route));
  if (surveysEntry) {
    pushUnique({
      LabelKey: surveysEntry.item.LabelKey,
      Icon: surveysEntry.item.Icon || "checklist",
      Route: surveysEntry.item.Route,
      Label: surveysEntry.item.Label,
      ShortLabel: "Quiz",
    });
  }

  const presenceEntry = businessItems.find(({ item }) => isPresenceRoute(item.Route));
  if (presenceEntry) {
    pushUnique({
      LabelKey: presenceEntry.item.LabelKey,
      Icon: presenceEntry.item.Icon || "edit_calendar",
      Route: presenceEntry.item.Route,
      Label: presenceEntry.item.Label,
      ShortLabel: "Émarg.",
    });
  }

  return out;
}
